import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Banknote, Check, CreditCard, Download, Pencil, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiDownload, isApiConfigured } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney, labelFor, PAYMENT_TERMS } from "@/features/clients/constants";
import { useInvoice, useUpdateInvoiceStatus } from "@/features/invoices/api";
import {
  INV_STATUSES,
  canEditLines,
  labelFor as invLabelFor,
  statusTone,
} from "@/features/invoices/constants";
import type { InvoiceStatus } from "@/features/invoices/types";

export function InvoiceDetailPage({ invId }: { invId: string }) {
  return (
    <ErrorBoundary title="Invoice detail crashed">
      <InvoiceDetailInner invId={invId} />
    </ErrorBoundary>
  );
}

function InvoiceDetailInner({ invId }: { invId: string }) {
  const navigate = useNavigate();
  const { data: inv, isLoading, error } = useInvoice(invId);
  const updateStatus = useUpdateInvoiceStatus();

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (error || !inv) {
    return (
      <EmptyState
        title="Invoice not found"
        description={(error as Error)?.message ?? "Unknown invoice"}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/invoices">Back to list</Link>
          </Button>
        }
      />
    );
  }

  async function setStatus(status: InvoiceStatus) {
    await updateStatus.mutateAsync({ id: invId, status });
  }

  async function downloadPdf() {
    try {
      await apiDownload(
        `/pdf/invoices/${invId}`,
        `${inv?.invoice_number || "invoice"}.pdf`,
      );
    } catch (e) {
      toast.error((e as Error).message || "PDF download failed");
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Button asChild variant="ghost" size="icon-sm">
            <Link to="/invoices">
              <ArrowLeft />
            </Link>
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-mono text-lg font-semibold">{inv.invoice_number}</h1>
              <StatusDot tone={statusTone(inv.status)}>
                {invLabelFor(INV_STATUSES, inv.status)}
              </StatusDot>
            </div>
            <p className="text-xs text-muted-foreground">
              {inv.client_code} · {inv.client_name} ·{" "}
              {format(parseISO(inv.issue_date), "dd MMM yyyy")}
              {inv.due_date ? ` · due ${format(parseISO(inv.due_date), "dd MMM yyyy")}` : ""}
              {inv.so_number ? ` · ${inv.so_number}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {isApiConfigured() ? (
            <Button type="button" variant="outline" size="sm" onClick={() => void downloadPdf()}>
              <Download />
              Download PDF
            </Button>
          ) : null}
          {canEditLines(inv.status) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: "/invoices/$invId/edit", params: { invId } })}
            >
              <Pencil />
              Edit
            </Button>
          ) : null}
          {inv.status === "draft" ? (
            <Button
              type="button"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("issued")}
            >
              <Send />
              Issue (post AR)
            </Button>
          ) : null}
          {inv.status === "issued" ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate({
                    to: "/payments",
                    search: { client: inv.client_id, invoice: invId },
                  })
                }
              >
                <Banknote />
                Record payment
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate({
                    to: "/credit-notes/new",
                    search: { client: inv.client_id, invoice: invId },
                  })
                }
              >
                <CreditCard />
                Credit note
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={updateStatus.isPending}
                onClick={() => setStatus("cancelled")}
              >
                <XCircle />
                Cancel
              </Button>
            </>
          ) : null}
          {inv.status === "draft" ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("cancelled")}
            >
              Cancel
            </Button>
          ) : null}
        </div>
      </header>

      {inv.status === "issued" ? (
        <p className="rounded border border-dashed px-3 py-2 text-xs text-muted-foreground">
          <Check className="mr-1 inline size-3.5" />
          AR posted for {formatMoney(inv.grand_total)} · shows on client statement
        </p>
      ) : null}

      <div className="grid gap-3 text-xs md:grid-cols-2">
        <section className="rounded border p-3">
          <h2 className="mb-1 font-medium">Bill to</h2>
          <pre className="whitespace-pre-wrap font-sans text-muted-foreground">
            {inv.bill_to_text || "—"}
          </pre>
        </section>
        <section className="rounded border p-3">
          <h2 className="mb-1 font-medium">Commercial</h2>
          <dl className="space-y-1 text-muted-foreground">
            <div className="flex justify-between gap-2">
              <dt>Payment</dt>
              <dd>{labelFor(PAYMENT_TERMS, inv.payment_terms)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Client PO</dt>
              <dd>{inv.client_po_ref || "—"}</dd>
            </div>
            {inv.sales_order_id && inv.so_number ? (
              <div className="flex justify-between gap-2">
                <dt>Sales order</dt>
                <dd>
                  <Link
                    to="/sales-orders/$soId"
                    params={{ soId: inv.sales_order_id }}
                    className="font-mono hover:underline"
                  >
                    {inv.so_number}
                  </Link>
                </dd>
              </div>
            ) : null}
          </dl>
        </section>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Material</TableHead>
              <TableHead>Grade</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Wt</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(inv.lines ?? []).map((line) => (
              <TableRow key={line.id} className="text-xs">
                <TableCell>{line.line_no}</TableCell>
                <TableCell>
                  {line.material_name}
                  {line.shape_label ? ` · ${line.shape_label}` : ""}
                </TableCell>
                <TableCell>{line.grade_name ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{line.qty || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.weight > 0 ? line.weight : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">{line.unit_rate}</TableCell>
                <TableCell className="text-right tabular-nums">{line.gst_slab}%</TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {formatMoney(line.line_subtotal)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <footer className="flex flex-wrap justify-end gap-4 text-xs">
        <div className="space-y-1 text-right">
          <div>
            Subtotal <strong className="tabular-nums">{formatMoney(inv.subtotal)}</strong>
          </div>
          {inv.discount > 0 ? (
            <div>
              Discount <strong className="tabular-nums">−{formatMoney(inv.discount)}</strong>
            </div>
          ) : null}
          <div>
            Tax <strong className="tabular-nums">{formatMoney(inv.tax_total)}</strong>
          </div>
          <div className="text-sm">
            Grand total{" "}
            <strong className="tabular-nums text-primary">{formatMoney(inv.grand_total)}</strong>
          </div>
        </div>
      </footer>
    </div>
  );
}
